<template>
  <label class="ux-field-title" v-if="showLabel"
    >{{ label }}
    <span class="ux-required-marker" v-if="required">*</span></label
  >
  <div class="ux-field-description" v-if="showDescription">
    {{ description }}
  </div>

  <div v-if="multiValue">
    <flex-row class="ux-text-row" :key="index" v-for="(entry, index) in model">
      <flex-cell>
        <template v-if="showTime">
          <input
            :min="formattedMinDate"
            :max="formattedMaxDate"
            type="datetime-local"
            class="ux-field-focus ux-text-input-multiple"
            @focus="touch"
            ref="input"
            @keydown.enter.stop.prevent="add()"
            v-model="model[index]"
          />
        </template>
        <template v-else>
          <input
            :min="formattedMinDate"
            :max="formattedMaxDate"
            type="date"
            class="ux-field-focus ux-text-input-multiple"
            @focus="touch"
            ref="input"
            @keydown.enter.stop.prevent="add()"
            v-model="dateOnlyModel[index]"
          />
        </template>
      </flex-cell>
      <flex-cell shrink vcenter>
        <ux-button
          color="default"
          tag="a"
          icon
          v-if="canRemoveValue"
          @click="remove(entry)"
        >
          <ux-icon icon="fa-times" />
        </ux-button>
      </flex-cell>
    </flex-row>
    <ux-button v-if="canAddValue" @click="add()">{{ addLabel }}</ux-button>
  </div>
  <template v-else>
    <template v-if="showTime">
      <input
        :min="formattedMinDate"
        :max="formattedMaxDate"
        type="datetime-local"
        class="ux-field-focus ux-text-input-single"
        @focus="touch"
        v-model="model"
      />
    </template>
    <template v-else>
      <input
        :min="formattedMinDate"
        :max="formattedMaxDate"
        type="date"
        class="ux-field-focus ux-text-input-single"
        @focus="touch"
        v-model="dateOnlyModel"
      />
    </template>
  </template>
</template>
<script>
import InputMixin from "./input-mixin";

//////////////////////////

function zero(input) {
  if (String(input).length < 2) {
    input = `0${input}`;
  }
  return input;
}

//////////////////////////

export default {
  props: {
    modelValue: {
      type: [Date, String, Array],
    },
    disableTime: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [InputMixin],
  computed: {
    formattedMinDate() {
      if (!this.minDate) return null;
      if (this.showTime) {
        return this.formatDateTime(this.minDate);
      }
      return this.formatDate(this.minDate);
    },
    formattedMaxDate() {
      if (!this.maxDate) return null;
      if (this.showTime) {
        return this.formatDateTime(this.maxDate);
      }
      return this.formatDate(this.maxDate);
    },
    dateOnlyModel: {
      get() {
        let val = this.cleanOutput(this.model);

        if (this.multiValue) {
          val = val.map(function (d) {
            if (!d) {
              return d;
            }

            return new Date(d).toISOString().slice(0, 10);
          });
        } else {
          if (val) {
            val = new Date(val).toISOString().slice(0, 10);
          }
        }

        return val;
      },
      set(newValue) {
        if (!newValue) {
          this.value = undefined;
          this.dispatch();
          return;
        }

        //Invalid date so no change here
        if (newValue === "invalid") {
          return;
        }

        ////////////////////////

        var val = this.cleanOutput(newValue);
        var existing = this.cleanOutput(this.value);

        if (val != existing) {
          newValue = this.cleanInput(val);

          if (this.multiValue) {
            newValue = newValue.map(function (d) {
              return new Date(d).toISOString();
            });
          } else {
            newValue = new Date(newValue).toISOString();
          }

          this.value = newValue;
          this.dispatch();
        }
      },
    },
    showTime() {
      return !this.dateOnly;
    },
    dateOnly() {
      if (this.disableTime) {
        return true;
      }

      return !!this.field?.dateOnly;
    },
    model: {
      get() {
        let val = this.cleanOutput(this.value);
        return val;
      },
      set(newValue) {
        if (!newValue) {
          this.value = undefined;
          this.dispatch();
          return;
        }

        //Invalid date so no change here
        if (newValue === "invalid") {
          return;
        }

        ////////////////////////

        var val = this.cleanOutput(newValue);
        var existing = this.cleanOutput(this.value);

        if (val != existing) {
          newValue = this.cleanInput(val);
          this.value = newValue;
          this.dispatch();
        }
      },
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      return `${d.getFullYear()}-${zero(d.getMonth() + 1)}-${zero(d.getDate())}`;
    },
    formatDateTime(date) {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      return `${this.formatDate(d)}T${zero(d.getHours())}:${zero(d.getMinutes())}`;
    },
    cleanOutputValue(d) {
      d = d ? new Date(d) : new Date();

      if (isNaN(d.getFullYear())) {
        return;
      }

      return `${d.getFullYear()}-${zero(d.getMonth() + 1)}-${zero(d.getDate())}T${zero(d.getHours())}:${zero(d.getMinutes())}`;
    },
    cleanInputValue(d) {
      let dt = new Date(d);
      if (dt.toString() == "Invalid Date") {
        return "invalid";
      }
      return dt.toISOString();
    },
    getNewDefaultEntry() {
      return new Date();
    },
  },
};
</script>
<style lang="scss" scoped>
.ux-text-row {
  margin-bottom: 0.5em;
}

input {
  border-radius: 0.1em;
  padding: 0.5em;
  box-sizing: border-box;
  // background: rgba(#000, 0.05);
  // border: 1px solid rgba(#000, 0.1);
  font-size: inherit;
  color: inherit;

  // appearance: none;
  font-family: inherit;
  line-height: 1;
  height: 2.5em;

  &::-webkit-calendar-picker-indicator {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23bbbbbb" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
  }

  // color: inherit;

  //  background: color-mix(in lab, currentColor 2%, transparent);
  // border: 1px solid color-mix(in lab, currentColor 18%, transparent);

  @extend .basic-input-style;

  &:focus {
    background: none;
    border: 1px solid var(--primary);
    outline: none;
  }
}

.ux-text-row .ux-btn {
  margin-left: 0.25em;
}

.ux-text-input-multiple {
  width: 100%;
}

.ux-text-input-single {
  width: 100%;
  // width: calc(100% - 0.5em);
  // margin: 0 0.5em 0 0;
}
</style>
